import authHttp from "./authHttp";

/**
 * Get all workout programs assigned to the end-user.
 * GET /api/v1/end-user/workout-programs
 */
export const fetchWorkoutPrograms = async () => {
  const response = await authHttp.get("/end-user/workout-programs");
  return response.data;
};

/**
 * Get full program details (sessions, blocks, metrics) for a specific program.
 * GET /api/v1/end-user/workout-programs/:programId/full
 */
export const fetchProgramFull = async (programId) => {
  const response = await authHttp.get(`/end-user/workout-programs/${programId}/full`);
  return response.data;
};

// ─── Session Lifecycle ──────────────────────────────────────────────────────

/**
 * Start a workout session (stamps started_at, sets status to ongoing).
 * POST /api/v1/end-user/workout-sessions-v2/:sessionId/start
 */
export const startSession = async (sessionId) => {
  const response = await authHttp.post(`/end-user/workout-sessions-v2/${sessionId}/start`);
  return response.data;
};

/**
 * Complete a workout session with optional feedback.
 * POST /api/v1/end-user/workout-sessions-v2/:sessionId/complete
 * Body: { rpe?: number, actual_duration_minutes?: number, client_feedback?: string }
 */
export const completeSession = async (sessionId, feedback = {}) => {
  const response = await authHttp.post(
    `/end-user/workout-sessions-v2/${sessionId}/complete`,
    feedback
  );
  return response.data;
};

// ─── Set Logging (instance-based) ──────────────────────────────────────────

/**
 * Get logged sets for a block instance.
 * GET /api/v1/end-user/workout-instances/:instanceId/sets
 */
export const fetchInstanceSets = async (instanceId) => {
  const response = await authHttp.get(`/end-user/workout-instances/${instanceId}/sets`);
  return response.data;
};

/**
 * Upsert a single set for a block instance.
 * PATCH /api/v1/end-user/workout-instances/:instanceId/sets/:setNumber
 * Body: { round_number, reps_completed, weight_used, weight_unit, rpe_actual, duration_seconds, distance, distance_unit, is_completed, is_skipped, notes }
 */
export const upsertSet = async (instanceId, setNumber, data) => {
  const response = await authHttp.patch(
    `/end-user/workout-instances/${instanceId}/sets/${setNumber}`,
    data
  );
  return response.data;
};

/**
 * Bulk upsert all sets for a block instance.
 * PUT /api/v1/end-user/workout-instances/:instanceId/sets
 * Body: { sets: [{ set_number, round_number, reps_completed, weight_used, ... }] }
 */
export const bulkUpsertSets = async (instanceId, sets) => {
  const response = await authHttp.put(
    `/end-user/workout-instances/${instanceId}/sets`,
    { sets }
  );
  return response.data;
};

// ─── Legacy endpoints (kept for backward compat) ───────────────────────────

/**
 * @deprecated Use fetchInstanceSets instead
 */
export const fetchBlockSets = async (instanceId) => {
  return fetchInstanceSets(instanceId);
};

/**
 * @deprecated Use upsertSet instead
 */
export const updateSet = async (instanceId, setNumber, data) => {
  return upsertSet(instanceId, setNumber, data);
};

/**
 * Update block status (active/completed).
 * PATCH /api/v1/workout-blocks/public/:blockId/status
 */
export const updateBlockStatus = async (blockId, status) => {
  const response = await authHttp.patch(
    `/workout-blocks/public/${blockId}/status`,
    { status }
  );
  return response.data;
};

/**
 * Update session status.
 * PATCH /api/v1/workout-sessions/public/:sessionId/status
 */
export const updateSessionStatus = async (sessionId, status) => {
  const response = await authHttp.patch(
    `/workout-sessions/public/${sessionId}/status`,
    { status }
  );
  return response.data;
};

/**
 * @deprecated Use completeSession instead
 */
export const updateWorkoutSessionStatus = async (sessionId, status) => {
  return updateSessionStatus(sessionId, status);
};
